import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { CanonicalService } from './canonical.service';
import { LinksService } from './links.service';
import { UrlUtility } from './url.utility';

export interface SeoConfig {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  locale?: 'en' | 'ar';
  structuredData?: any;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private renderer: Renderer2;

  constructor(
    private titleService: Title,
    private meta: Meta,
    private canonicalService: CanonicalService,
    private linksService: LinksService,
    private urlUtility: UrlUtility,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public setSeo(config: SeoConfig): void {
    const siteName = 'Card4Games';
    const baseUrl = this.linksService.baseUrl;
    const locale = config.locale || 'en';
    
    const pageTitle = config.title ? `${config.title} | ${siteName}` : siteName;
    const pageDescription = config.description || 'Card4Games - Digital Cards & Game Subscriptions Store';
    const pageImage = config.image || this.urlUtility.buildUrl(baseUrl, undefined, 'assets/logo-web-2.png');
    
    // Normalize the page URL
    const pageUrl = config.url 
      ? this.urlUtility.normalize(config.url)
      : this.urlUtility.buildUrl(baseUrl, locale);
    
    const pageType = config.type || 'website';

    // Validate the URL
    this.urlUtility.logValidation(pageUrl, 'SEO setSeo');

    // 1. Title
    this.titleService.setTitle(pageTitle);

    // 2. Standard Meta Tags
    this.meta.updateTag({ name: 'description', content: pageDescription });
    if (config.keywords) {
      this.meta.updateTag({ name: 'keywords', content: config.keywords });
    }

    // 3. Open Graph Tags (with normalized URL)
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: pageDescription });
    this.meta.updateTag({ property: 'og:image', content: pageImage });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });
    this.meta.updateTag({ property: 'og:type', content: pageType });
    this.meta.updateTag({ property: 'og:site_name', content: siteName });
    this.meta.updateTag({ property: 'og:locale', content: locale === 'ar' ? 'ar_AR' : 'en_US' });

    // 4. Twitter Card Tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: pageDescription });
    this.meta.updateTag({ name: 'twitter:image', content: pageImage });

    // 5. Canonical Link (automatically normalized)
    this.canonicalService.setCanonicalUrl(pageUrl);

    // 6. Structured Data (JSON-LD)
    if (config.structuredData) {
      this.setStructuredData(config.structuredData);
    }
  }

  /**
   * Sets structured data (JSON-LD) in <head>.
   * Removes ALL existing JSON-LD scripts first to prevent duplicates.
   * Normalizes all URLs in the structured data.
   */
  public setStructuredData(data: any): void {
    // Remove ALL existing JSON-LD scripts (not just one)
    const existingScripts = this.document.head.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => {
      if (script.parentNode) {
        this.renderer.removeChild(script.parentNode, script);
      }
    });

    // Normalize all URLs in the structured data
    const normalizedData = this.normalizeStructuredDataUrls(data);

    // Create new script
    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'application/ld+json');
    this.renderer.setProperty(script, 'text', JSON.stringify(normalizedData, null, 2));
    this.renderer.appendChild(this.document.head, script);
  }

  /**
   * Recursively normalizes all URL fields in structured data
   */
  private normalizeStructuredDataUrls(data: any): any {
    if (!data) return data;

    if (typeof data === 'string') {
      // Check if it looks like a URL
      if (data.startsWith('http://') || data.startsWith('https://')) {
        const normalized = this.urlUtility.normalize(data);
        this.urlUtility.logValidation(normalized, 'Structured Data URL');
        return normalized;
      }
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.normalizeStructuredDataUrls(item));
    }

    if (typeof data === 'object') {
      const normalized: any = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          // Special handling for URL fields
          if (key === 'url' || key === '@id' || key === 'href' || key === 'sameAs' || key === 'target') {
            normalized[key] = this.normalizeStructuredDataUrls(data[key]);
          } else {
            normalized[key] = this.normalizeStructuredDataUrls(data[key]);
          }
        }
      }
      return normalized;
    }

    return data;
  }
}
